import { NextPage, NextPageContext } from 'next';
import dynamic from 'next/dynamic';
import Error, { ErrorProps } from 'next/error';
import { useRouter } from 'next/router';
import { useContext } from 'react';

import { VALID_WORKFLOW_URLS } from 'components/DashboardPages/Workflow/constants';
import Header from 'components/Header';
import SideBar from 'components/SideBar';
import { SENTRY_EVENT_EXTRA_PARAMS } from 'constants/index';
import { storeUserInfo } from 'context/UserInfoProvider';
import { isB2BDomain } from 'utils/environments';
import { landingPageClasses } from 'utils/pageUtils';
import { sendErrorEvent } from 'utils/sentry';

const Container = dynamic(() => import('containers/StaticPages/NotFoundPage'));

const ErrorPage = dynamic(() => import('containers/StaticPages/ErrorPage'));

const WorkflowErrorPage = dynamic(
  () => import('containers/StaticPages/WorkflowErrorPage'),
);

const MyError: NextPage<
  ErrorProps & { hasGetInitialPropsRun?: boolean } & Pick<
      NextPageContext,
      'err'
    > & { heading?: string; message?: string }
> = ({ statusCode, hasGetInitialPropsRun, err, ...props }) => {
  const router = useRouter();

  const { userInfoState } = useContext(storeUserInfo);
  const userInfo = userInfoState?.data;
  const isWorkflowEnabled = userInfo?.enterprise?.is_workflow_enabled;
  const isWorkflowPage =
    VALID_WORKFLOW_URLS.Workflows.test(router.asPath) && isWorkflowEnabled;

  if (!hasGetInitialPropsRun && err) {
    // Workaround of https://github.com/vercel/next.js/issues/8592
    const extraErrorParams = {
      [SENTRY_EVENT_EXTRA_PARAMS.pageBroken]: 'true',
      ...(isWorkflowPage && {
        [SENTRY_EVENT_EXTRA_PARAMS.workflowPageBroken]: 'true',
      }),
    };

    const errorInfo = {
      ...extraErrorParams,
      ...(isWorkflowPage && statusCode && { statusCode: String(statusCode) }),
    };

    sendErrorEvent(err, errorInfo, {}, extraErrorParams);
  }

  if (isWorkflowPage) {
    return (
      <div className='ed-grid'>
        <div className='ed-grid-main'>
          <WorkflowErrorPage flagCode={statusCode?.toString()} />
        </div>
      </div>
    );
  }

  if (statusCode === 404)
    return (
      <div className='ed-grid'>
        <SideBar />
        <Header />
        <div className={`ed-grid-main ${landingPageClasses}`}>
          <Container key='*' {...props} />
        </div>
      </div>
    );

  return (
    <div className='ed-grid'>
      <SideBar />
      <Header />
      <div className={`ed-grid-main ${landingPageClasses}`}>
        <ErrorPage statusCode={statusCode} />
      </div>
    </div>
  );
};

MyError.getInitialProps = async (props) => {
  const errorInitialProps = await Error.getInitialProps(props);

  const { req, res } = props;
  const _isB2BDomain = isB2BDomain({ serverRequest: req });

  if (props.res?.statusCode === 404) {
    return { statusCode: 404, isB2BDomain: _isB2BDomain };
  }

  if (props.err) {
    const extraErrorParams = { [SENTRY_EVENT_EXTRA_PARAMS.pageBroken]: 'true' };
    const extraTags = { [SENTRY_EVENT_EXTRA_PARAMS.pageBroken]: 'true' };

    if (req?.url)
      extraErrorParams[SENTRY_EVENT_EXTRA_PARAMS.pageRoute] =
        req.headers?.['host'] + req.url;

    if (
      _isB2BDomain ||
      req?.headers?.['cookie']?.includes('enterprise_nav=true')
    ) {
      extraErrorParams[SENTRY_EVENT_EXTRA_PARAMS.b2bUser] = 'true';
      extraTags[SENTRY_EVENT_EXTRA_PARAMS.b2bUser] = 'true';
    }

    sendErrorEvent(props.err, extraErrorParams, {}, extraTags);
  }

  res?.setHeader('Cache-Control', 'no-store');

  return {
    hasGetInitialPropsRun: true,
    ...errorInitialProps,
    isB2BDomain: _isB2BDomain,
  };
};

export default MyError;
